import { Col, Row, Table } from "antd";
import Subscriptions from "entities/Subscriptions";
import { find, get } from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { TbFileExport } from "react-icons/tb";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Pagination from "shared/components/Pagination";
import SelectAsync from "shared/components/Select/Async";
import { NAME_SPACES } from "shared/locales/constants";
import { TRACER_OPTIONS } from "utils/api/graphql/queries/tracers";
import {
  CURRENCY_SYMBOL,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
} from "utils/constants";
import { displayDate, resolveDisplayedContractDate } from "utils/helpers/date";
import { resolveStoredPlanPremium } from "utils/helpers/subscriptionPremium";
import { formatPrice } from "utils/helpers/values";
import { formatClientName } from "./clientName";

const List = ({
  filter,
  pagination,
  setPagination,
  onSearch,
  selectedTracerIds,
  onTracerChange,
  exportSubscriptions,
  exportLoading,
  exportCollectiveLoading,
  exportCollectiveSubscriptions,
  externalExports,
  onExternalExport,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.SUBSCRIPTION);
  const LIST = t("LIST", { returnObjects: true });

  const getCost = (project, subscription) => {
    const baseId = get(subscription, "insurancePlan.id");
    const generalCost = find(
      get(project, "generalCosts"),
      (cost) => get(cost, "insurancePlan.id") === baseId,
    );
    if (!generalCost) {
      const premium = resolveStoredPlanPremium({
        project,
        insurancePlanId: baseId,
      });
      if (!premium) return "";
      return formatPrice(premium.amount, {
        currency: CURRENCY_SYMBOL[premium.currency] || "€",
      });
    }
    const baseCost = get(generalCost, "cost", 0);
    const currency = get(generalCost, "currency", "DEFAULT");

    // Upsells can be carried in two forms:
    // 1. Combinations (Mobilité Mutuelle): the chosen combination selects a
    //    single complementary module from `fields.upsells[baseId]`.
    // 2. Legacy: `fields.upsells[baseId]` is the full list of selected upsells.
    const baseCombinations = get(project, "fields.combinations", []).filter(
      (combination) => get(combination, "baseInsurancePlanId") === baseId,
    );
    const upsellsByBase = get(project, `fields.upsells.${baseId}`, []);

    let upsellsCost = 0;
    if (baseCombinations.length > 0) {
      const chosenCombinationId =
        get(subscription, "additionalInfo.combinationId") ||
        get(project, "fields.selectedCombinationId");
      const selectedCombinations = chosenCombinationId
        ? baseCombinations.filter(
            (combination) => get(combination, "id") === chosenCombinationId,
          )
        : [];
      upsellsCost = selectedCombinations.reduce((acc, combination) => {
        const complementaryId = get(
          combination,
          "complementaryInsurancePlanId",
        );
        if (!complementaryId) return acc;
        const complement = find(
          upsellsByBase,
          (module) => get(module, "insurancePlan.id") === complementaryId,
        );
        return acc + get(complement, "cost", 0);
      }, 0);
    } else {
      upsellsCost = upsellsByBase.reduce(
        (acc, upsell) => acc + get(upsell, "cost", 0),
        0,
      );
    }

    const totalCost = baseCost + upsellsCost;
    return formatPrice(totalCost, { currency: CURRENCY_SYMBOL[currency] });
  };

  const columns = [
    {
      title: LIST.SIGNATURE_DATE,
      key: "signedDate",
      render: (_, record) =>
        displayDate({ date: resolveDisplayedContractDate(record) }),
    },
    {
      title: LIST.START_DATE,
      dataIndex: "project",
      render: (item) =>
        displayDate({
          date: get(item, "contract.issuanceDate"),
          format: DATE_FORMAT,
        }),
      key: "issuanceDate",
    },
    {
      title: LIST.CLIENT,
      dataIndex: "project",
      render: formatClientName,
      key: "client",
    },
    {
      title: LIST.ADVISOR,
      dataIndex: "project",
      render: (record) =>
        record?.author && record?.hasResponsible
          ? `${record?.author.firstname} ${record?.author.lastname}`
          : `-----------`,
      key: "advisor",
    },
    {
      title: LIST.TAKEOVER,
      dataIndex: "project",
      render: (record) => {
        if (!get(record, "author")) return "";
        return get(record, "hasResponsible")
          ? LIST.TAKEOVER_YES
          : LIST.TAKEOVER_NO;
      },
      key: "takeover",
    },
    {
      title: LIST.TAKEOVER_DATE,
      dataIndex: "project",
      render: (record) => {
        const takenOverAt = get(record, "fields.takenOverAt");
        return takenOverAt ? moment(takenOverAt).format(DATE_TIME_FORMAT) : "";
      },
      key: "takeoverDate",
    },
    {
      title: LIST.STATUS,
      dataIndex: "status",
      render: (record) => <span>{LIST.STATUSES[record]}</span>,
      key: "status",
    },
    {
      title: LIST.OFFER,
      dataIndex: "insurancePlan",
      render: (record) => get(record, "name", "-----"),
      key: "offer",
    },
    {
      title: LIST.MONTHLY_PRICE,
      dataIndex: "project",
      render: getCost,
      key: "price",
    },
  ];

  return (
    <>
      <Header
        key={"header"}
        title={LIST.HEADER.TITLE}
        actions={[
          ...externalExports.map((externalExport) => ({
            icon: <TbFileExport size={20} />,
            text: t(externalExport.label),
            onClick: () => onExternalExport(externalExport),
          })),
          {
            icon: <TbFileExport size={20} />,
            text: LIST.HEADER.EXPORT_CONTRACTS,
            onClick: exportSubscriptions,
            loading: exportLoading,
          },
          {
            icon: <TbFileExport size={20} />,
            text: LIST.HEADER.EXPORT_COLLECTIVE_CONTRACTS,
            onClick: exportCollectiveSubscriptions,
            loading: exportCollectiveLoading,
          },
        ]}
      />
      <Card type="simple">
        <Row>
          <Col lg={20} xs={24}>
            <Row gutter={[8, 8]}>
              <Col lg={12} xs={24}>
                <Input
                  type="search"
                  placeholder={LIST.FILTERS.SEARCH}
                  onChange={({ target }) => onSearch(target)}
                />
              </Col>
              <Col lg={12} xs={24}>
                <SelectAsync
                  mode="multiple"
                  allowClear
                  maxTagCount="responsive"
                  label={LIST.FILTERS.TRACERS}
                  placeholder={LIST.FILTERS.TRACERS_PLACEHOLDER}
                  node={{
                    query: TRACER_OPTIONS,
                    variables: {
                      take: 1000,
                      orderBy: [{ name: "ASC" }],
                    },
                    accessors: {
                      root: "tracers.data",
                      label: "name",
                      value: "id",
                    },
                  }}
                  value={selectedTracerIds}
                  onChange={(value) => onTracerChange(value || [])}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>
      <div className="m-bottom-24" />
      <Subscriptions
        View={({ count, data, setValues, skip }) => (
          <Card type={"simple"}>
            <Table
              dataSource={data}
              columns={columns}
              className="table__wrapper"
              scroll={{ x: true }}
              rowKey={"id"}
              pagination={false}
            />
            <Pagination count={count} setValues={setValues} skip={skip || 0} />
          </Card>
        )}
        setValues={setPagination}
        skip={pagination.skip}
        filter={{ ...filter, ...pagination }}
      />
    </>
  );
};

List.defaultProps = {
  externalExports: [],
  onExternalExport: () => {},
};

export default List;
